<template>
  <div class="h-full flex flex-col">
    <!-- Search bar -->
    <div class="px-4 py-3 bg-white shrink-0" style="border-bottom: 1px solid #E8E9E9;">
      <div class="flex items-end gap-3">
        <h2 class="text-sm font-semibold" style="color: #353537;">Search &amp; Enroll</h2>
        <div class="flex-1 max-w-md">
          <label for="patient-search-text" class="block mb-1 text-xs font-medium text-gray-600">Search by name, MBI, or phone number</label>
          <input
            id="patient-search-text"
            ref="searchInput"
            v-model="searchText"
            type="text"
            placeholder="Search by name, MBI, or phone number..."
            class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            @keydown.enter="doSearch"
          />
        </div>
        <div class="w-36">
          <label for="patient-search-dob" class="block mb-1 text-xs font-medium text-gray-600">Date of birth</label>
          <input
            id="patient-search-dob"
            v-model="searchDob"
            v-maska="{ mask: '##/##/####' }"
            type="text"
            inputmode="numeric"
            autocomplete="bday"
            placeholder="MM/DD/YYYY"
            class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            @keydown.enter="doSearch"
          />
        </div>
        <button
          @click="doSearch"
          :disabled="(!searchText.trim() && !searchDob) || searching"
          class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {{ searching ? 'Searching...' : 'Search' }}
        </button>
        <button
          @click="openManualModal"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
        >
          + Add patient manually
        </button>
        <button
          v-if="isAdmin"
          @click="openTestModal"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
        >
          + New Test Patient
        </button>
      </div>
      <p class="mt-1 text-xs" style="color: #A5A5A6;">Use date of birth to narrow results or search by date alone. Text searches need at least 3 characters unless a date of birth is provided.</p>
    </div>

    <!-- Results -->
    <div class="flex-1 overflow-y-auto">
      <!-- Empty state -->
      <div v-if="!hasSearched" class="flex items-center justify-center h-full text-xs" style="color: #A5A5A6;">
        Search by name, MBI, or phone number, or use the separate date of birth field
      </div>

      <!-- No results. A patient the pipeline has never seen (no attribution,
           no claims) cannot appear here at all, so this is also the entry point
           for adding one by hand. -->
      <div v-else-if="!searching && results.length === 0" class="flex flex-col items-center justify-center h-full gap-3">
        <p class="text-xs" style="color: #A5A5A6;">No patients found for "{{ lastQuery }}"</p>
        <button
          @click="openManualModal"
          class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
        >
          + Add patient manually
        </button>
        <p class="text-xs max-w-md text-center" style="color: #A5A5A6;">
          Creates the patient and enrols them in a program. Use this for a patient
          who isn't in attribution or claims yet — everything is entered by hand.
        </p>
      </div>

      <!-- Results table -->
      <table v-else class="w-full">
        <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">MBI</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">DOB (YYYY-MM-DD)</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Practice</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Attributed</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Programs</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Previous Programs</th>
            <th class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Enroll</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="(patient, idx) in results" :key="patient.mbi || patient.id || idx" class="hover:bg-gray-50">
            <td class="px-4 py-2">
              <button
                type="button"
                class="sensitive text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline text-left"
                :class="{ 'text-gray-900 cursor-default hover:no-underline': !patient.id }"
                :title="patient.id ? 'Open patient history' : 'Not yet in the app — no history to show'"
                :disabled="!patient.id"
                @click="patient.id && openPatient(patient)"
              >{{ patient.name }}</button>
            </td>
            <td class="px-3 py-2">
              <span class="sensitive text-xs text-gray-600 font-mono">{{ patient.mbi || '—' }}</span>
            </td>
            <td class="px-3 py-2">
              <span class="sensitive text-xs text-gray-600">{{ patient.dob || '—' }}</span>
            </td>
            <td class="px-3 py-2">
              <span class="sensitive text-xs text-gray-600">{{ patient.phone || '—' }}</span>
            </td>
            <td class="px-3 py-2">
              <span class="sensitive text-xs text-gray-600">{{ patient.practice || '—' }}</span>
            </td>
            <td class="px-3 py-2">
              <span
                class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium leading-none rounded"
                :class="patient.attributed
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'"
                :title="patient.attributed
                  ? ('Attributed to ' + (patient.attributed_contract_id || 'an ACO contract') + ' this year')
                  : 'Not attributed this year — no discharge/TOC events will be generated for this patient'"
              >
                <FontAwesomeIcon :icon="['fas', patient.attributed ? 'circle-check' : 'circle-exclamation']" class="text-xs" />
                <span v-if="patient.attributed && patient.attributed_contract_id">{{ patient.attributed_contract_id }}</span>
                <span v-else-if="!patient.attributed">No</span>
              </span>
            </td>
            <td class="px-3 py-2">
              <span
                v-for="prog in patient.active_programs"
                :key="prog"
                class="inline-block px-1.5 py-0.5 mr-1 mb-0.5 text-xs font-medium rounded"
                style="background: #EEF2FF; color: #4338CA;"
              >{{ prog }}</span>
              <span v-if="!patient.active_programs.length" class="text-xs text-gray-400">None</span>
            </td>
            <td class="px-3 py-2">
              <span
                v-for="prog in patient.previous_programs"
                :key="'prev-' + prog"
                class="inline-block px-1.5 py-0.5 mr-1 mb-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500"
                title="Previously worked in this program (completed)"
              >{{ prog }}</span>
              <span v-if="!patient.previous_programs || !patient.previous_programs.length" class="text-xs text-gray-400">None</span>
            </td>
            <td class="px-3 py-2">
              <select
                v-model="patient.selectedProgram"
                @change="openEnrollModal(patient)"
                class="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Enroll in...</option>
                <option
                  v-for="prog in availablePrograms"
                  :key="prog.slug"
                  :value="prog.slug"
                  :disabled="patient.active_programs.includes(prog.label)"
                >{{ prog.label }}</option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Loading -->
      <div v-if="searching" class="flex items-center justify-center py-8">
        <span class="text-xs text-gray-500">Searching...</span>
      </div>
    </div>

    <!-- Test patients management (superuser) -->
    <div v-if="isAdmin" class="shrink-0 border-t border-gray-200 bg-white">
      <div class="px-4 py-2 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wider">
          Test Patients ({{ testPatients.length }})
        </h3>
        <button @click="loadTestPatients" class="text-xs text-primary-600 hover:text-primary-700">Refresh</button>
      </div>
      <div v-if="!testPatients.length" class="px-4 py-3 text-xs text-gray-400">
        No test patients yet. Use “+ New Test Patient” above to create one.
      </div>
      <div v-else class="max-h-48 overflow-y-auto">
        <table class="w-full">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Program</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Practice</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="tp in testPatients" :key="tp.opportunity_id" class="hover:bg-gray-50">
              <td class="px-4 py-1.5 text-xs font-medium text-gray-900">{{ tp.name }}</td>
              <td class="px-3 py-1.5 text-xs text-gray-600 font-mono">{{ tp.patient_identifier }}</td>
              <td class="px-3 py-1.5 text-xs text-gray-600">{{ tp.program }}</td>
              <td class="px-3 py-1.5 text-xs text-gray-600">{{ tp.practice || '—' }}</td>
              <td class="px-3 py-1.5 text-xs text-gray-500">{{ formatDate(tp.created_at) }}</td>
              <td class="px-3 py-1.5 text-right">
                <button @click="deleteTestPatient(tp)" :disabled="deletingId === tp.opportunity_id"
                  class="px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50">
                  {{ deletingId === tp.opportunity_id ? 'Deleting…' : 'Delete' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Enroll confirmation toast -->
    <div
      v-if="enrollMessage"
      class="fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-xs font-medium"
      :class="enrollSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'"
    >
      {{ enrollMessage }}
    </div>

    <!-- Manual patient modal. Deliberately thin: it creates the patient entity,
         parents it, and enrols it in one program. No attribution, no claims, no
         EMPI resolution — everything here is typed by the user. -->
    <teleport to="body">
      <div
        v-if="showManualModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="showManualModal = false"
      >
        <div class="w-full max-w-lg max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl">
          <div class="flex items-center justify-between px-4 py-3" style="border-bottom: 1px solid #E8E9E9;">
            <h3 class="text-sm font-semibold" style="color: #353537;">Add patient manually</h3>
            <button @click="showManualModal = false" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
          </div>

          <div class="p-4 overflow-y-auto min-h-0">
            <p class="text-xs mb-3" style="color: #A5A5A6;">
              For a patient who isn't in attribution or claims. Creates the patient
              and enrols them in the program you pick. Nothing is looked up.
            </p>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">First name</label>
                <input v-model.trim="manualForm.first_name" type="text"
                  class="sensitive w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Last name <span class="text-red-500">*</span></label>
                <input v-model.trim="manualForm.last_name" type="text"
                  class="sensitive w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Date of birth</label>
                <input v-model="manualForm.dob" type="date"
                  class="sensitive w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Patient identifier <span class="text-red-500">*</span>
                </label>
                <input v-model.trim="manualForm.patient_identifier" type="text" placeholder="MBI or member ID"
                  class="sensitive w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md font-mono focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>

              <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Parent entity <span class="text-red-500">*</span>
                </label>
                <input v-model.trim="parentQuery" type="text" placeholder="Type to find a practice, department or provider…"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  @input="onParentQuery" />
                <p v-if="manualForm.parent_entity_id" class="mt-1 text-xs text-green-700">
                  Selected: {{ selectedParentName }}
                </p>
                <div v-else-if="parentResults.length" class="mt-1 border border-gray-200 rounded max-h-40 overflow-y-auto">
                  <button
                    v-for="ent in parentResults"
                    :key="ent.id"
                    type="button"
                    class="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-0"
                    @click="pickParent(ent)"
                  >
                    {{ ent.name }}
                    <span class="text-gray-400">· {{ ent.entity_type_name || ent.entity_type?.type_name || '' }}</span>
                    <span v-if="ent.breadcrumb && ent.breadcrumb.length" class="block text-gray-400">
                      {{ ent.breadcrumb.map(b => b.name || b).join(' › ') }}
                    </span>
                  </button>
                </div>
                <p v-else-if="parentSearching" class="mt-1 text-xs text-gray-400">Searching…</p>
                <p class="mt-1 text-xs" style="color: #A5A5A6;">
                  Without a parent the patient has no practice, so they won't show
                  in practice-scoped lists or be picked up by assignment rules.
                </p>
              </div>

              <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">Program <span class="text-red-500">*</span></label>
                <select v-model.number="manualForm.metric_id"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                  <option value="">Select a program…</option>
                  <option v-for="p in availablePrograms" :key="p.id" :value="p.id">{{ p.label }}</option>
                </select>
              </div>
              <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">Initial Due Date <span class="text-red-500">*</span></label>
                <button ref="manualDueDateTrigger" type="button" @click="toggleManualDueCalendar"
                  class="w-full px-2 py-1.5 text-left text-xs border border-gray-300 rounded-md bg-white hover:border-primary-400 focus:ring-1 focus:ring-primary-500">
                  {{ manualForm.first_task_due_date || 'Select initial due date…' }}
                </button>
              </div>
            </div>

            <p v-if="manualError" class="mt-3 text-xs text-red-600">{{ manualError }}</p>
          </div>

          <div class="flex items-center justify-end gap-2 px-4 py-3" style="border-top: 1px solid #E8E9E9;">
            <button @click="showManualModal = false"
              class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
            <button @click="submitManual" :disabled="!manualValid || manualSaving"
              class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50">
              {{ manualSaving ? 'Creating…' : 'Create and enrol' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Test patient builder modal -->
    <teleport to="body">
      <div
        v-if="showTestModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="showTestModal = false"
      >
        <div class="w-full max-w-lg max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl">
          <!-- Modal header -->
          <div class="flex items-center justify-between px-4 py-3" style="border-bottom: 1px solid #E8E9E9;">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-semibold" style="color: #353537;">Create Test Patient</h3>
              <span class="inline-block px-1.5 py-0.5 text-xs font-semibold rounded" style="background: #FEF3C7; color: #92400E;">Admin</span>
            </div>
            <button @click="showTestModal = false" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
          </div>

          <!-- Modal body -->
          <div class="p-4 overflow-y-auto min-h-0">
            <p class="text-xs mb-3" style="color: #A5A5A6;">
              Creates a synthetic CM patient with a fabricated ADT discharge so it appears in the worklist.
              No clinical data, no entity. Tagged ZZTEST.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">First name</label>
                <input v-model="testForm.first_name" type="text"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Last name</label>
                <input v-model="testForm.last_name" type="text"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Date of birth</label>
                <input v-model="testForm.dob" type="date"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Program</label>
                <select v-model="testForm.program_slug"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                  <option value="">Select a program…</option>
                  <option v-for="p in availablePrograms" :key="p.slug" :value="p.slug">{{ p.label }}</option>
                </select>
              </div>
              <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">Practice</label>
                <select v-model="selectedPracticeId" @change="onPracticeChange"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                  <option value="">Select a practice…</option>
                  <option v-for="pr in testPractices" :key="pr.practice_id" :value="pr.practice_id">
                    {{ pr.practice_name }}
                  </option>
                </select>
                <p v-if="selectedPractice" class="mt-1 text-xs text-gray-400">
                  Provider {{ selectedPractice.provider_name }} (NPI {{ selectedPractice.npi }}) · TIN {{ selectedPractice.tin }}
                </p>
                <p v-else class="mt-1 text-xs text-gray-400">Only TOC-tagged practices listed. Patient is parented here.</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Episode type</label>
                <select v-model="testForm.episode_type"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                  <option value="INPATIENT">Inpatient</option>
                  <option value="ED_VISIT">ED</option>
                  <option value="OBSERVATION_STAY">Observation</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Discharge time</label>
                <input v-model="testForm.discharge_time" type="datetime-local"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  {{ testForm.episode_type === 'ED_VISIT' ? 'ECHO score' : 'LACE score' }}
                </label>
                <input v-model.number="testForm.risk_score" type="number" min="0" max="19"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
                <p class="mt-1 text-xs text-gray-400">Sets the tier. Blank = use program directly.</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Facility</label>
                <input v-model="testForm.facility" type="text" placeholder="Test Hospital"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </div>
            </div>
          </div>

          <!-- Modal footer -->
          <div class="flex items-center justify-end gap-2 px-4 py-3" style="border-top: 1px solid #E8E9E9;">
            <button @click="showTestModal = false"
              class="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button @click="createTestPatient" :disabled="!canSubmitTest || creatingTest"
              class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {{ creatingTest ? 'Creating…' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Manual enrollment metadata modal -->
    <teleport to="body">
      <div
        v-if="showEnrollModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="cancelEnroll"
      >
        <div class="w-full max-w-lg max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl">
          <!-- Modal header -->
          <div class="flex items-center justify-between px-4 py-3" style="border-bottom: 1px solid #E8E9E9;">
            <h3 class="text-sm font-semibold" style="color: #353537;">
              Enroll {{ enrollPatientRef?.name }} in {{ enrollProgramLabel }}
            </h3>
            <button @click="cancelEnroll" class="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
          </div>

          <!-- Modal body -->
          <div class="p-4 overflow-y-auto min-h-0">
            <div v-if="contractLoading" class="py-8 text-center text-xs text-gray-400">
              Loading enrollment fields…
            </div>
            <template v-else>
              <!-- Search-resolved (fixed) block: identity / provider / contract -->
              <div class="mb-3 rounded-md bg-gray-50 px-3 py-2">
                <div class="text-xs font-medium text-gray-500 mb-1">From search (fixed)</div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-700">
                  <div><span class="text-gray-400">Patient:</span> {{ enrollPatientRef?.name }}</div>
                  <div v-if="enrollPatientRef?.mbi"><span class="text-gray-400">ID:</span> {{ enrollPatientRef.mbi }}</div>
                  <div v-if="enrollPatientRef?.npi"><span class="text-gray-400">Provider NPI:</span> {{ enrollPatientRef.npi }}</div>
                  <div v-if="enrollPatientRef?.tin"><span class="text-gray-400">TIN:</span> {{ enrollPatientRef.tin }}</div>
                  <div v-if="enrollPatientRef?.practice_name" class="col-span-2"><span class="text-gray-400">Practice:</span> {{ enrollPatientRef.practice_name }}</div>
                  <div v-if="enrollPatientRef?.attributed_contract_id" class="col-span-2"><span class="text-gray-400">Contract:</span> {{ enrollPatientRef.attributed_contract_id }}</div>
                </div>
              </div>

              <p class="text-xs mb-3" style="color: #A5A5A6;">
                Enter the details for this enrollment. These populate the worklist columns.
                Fields are derived from the program's opportunity definition.
              </p>

              <div class="mb-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">Initial Due Date <span class="text-red-500">*</span></label>
                <button ref="enrollDueDateTrigger" type="button" @click="toggleEnrollDueCalendar"
                  class="w-full px-2 py-1.5 text-left text-xs border border-gray-300 rounded-md bg-white hover:border-primary-400 focus:ring-1 focus:ring-primary-500">
                  {{ enrollDueDate || 'Select initial due date…' }}
                </button>
                <p class="mt-1 text-xs text-gray-400">Weekend and holiday dates will be moved to the next business day.</p>
              </div>

              <div v-if="!contractFields.length" class="text-xs text-gray-400 py-4">
                This program needs no additional fields — click Enroll to continue.
              </div>

              <div v-else class="grid grid-cols-2 gap-3">
                <div v-for="field in contractFields" :key="field.key"
                     :class="field.type === 'text' && contractFields.length > 1 ? '' : ''">
                  <label class="block text-xs font-medium text-gray-700 mb-1">{{ field.label }}</label>

                  <select v-if="field.type === 'select'" v-model="enrollForm[field.key]"
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                    <option value="">—</option>
                    <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                  </select>

                  <input v-else-if="field.type === 'datetime'" v-model="enrollForm[field.key]" type="datetime-local"
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />

                  <input v-else-if="field.type === 'number'" v-model.number="enrollForm[field.key]" type="number"
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />

                  <label v-else-if="field.type === 'boolean'" class="flex items-center gap-2 mt-1 text-xs text-gray-600">
                    <input v-model="enrollForm[field.key]" type="checkbox" class="rounded border-gray-300" />
                    Yes
                  </label>

                  <input v-else v-model="enrollForm[field.key]" type="text"
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
                </div>
              </div>
            </template>
          </div>

          <!-- Modal footer -->
          <div class="flex items-center justify-end gap-2 px-4 py-3" style="border-top: 1px solid #E8E9E9;">
            <button @click="cancelEnroll"
              class="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button @click="confirmEnroll" :disabled="enrolling || contractLoading || !enrollDueDate"
              class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {{ enrolling ? 'Enrolling…' : 'Enroll' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div v-if="showManualDueCalendar" class="fixed inset-0 z-[60]" @click.self="showManualDueCalendar = false">
        <div class="fixed z-[61] max-h-[calc(100vh-1rem)] overflow-y-auto shadow-lg" :style="manualDueCalendarPosition">
          <TaskCalendarPicker
            :model-value="manualForm.first_task_due_date"
            date-only
            @update:model-value="manualForm.first_task_due_date = $event"
            @day-picked="showManualDueCalendar = false"
          />
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div v-if="showEnrollDueCalendar" class="fixed inset-0 z-[60]" @click.self="showEnrollDueCalendar = false">
        <div class="fixed z-[61] max-h-[calc(100vh-1rem)] overflow-y-auto shadow-lg" :style="enrollDueCalendarPosition">
          <TaskCalendarPicker
            :model-value="enrollDueDate"
            date-only
            @update:model-value="enrollDueDate = $event"
            @day-picked="showEnrollDueCalendar = false"
          />
        </div>
      </div>
    </teleport>

    <!-- Patient history slide-over: open ANY searched patient (enrolled or not)
         to their full history — Details/attribution + Programs (active + previous
         via the tab's Show-completed toggle). Reuses the shared Patient_Drawer. -->
    <teleport to="body">
      <div v-if="historyPatient" class="fixed inset-0 z-50 flex justify-end">
        <div class="absolute inset-0 bg-black/30" @click="closePatient"></div>
        <div class="relative bg-white w-full max-w-2xl h-full shadow-xl overflow-y-auto">
          <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
            <div class="min-w-0">
              <p class="sensitive text-sm font-semibold text-gray-900 truncate">{{ historyPatient.name }}</p>
              <p class="sensitive text-xs text-gray-500">{{ historyPatient.mbi }}</p>
            </div>
            <button class="text-gray-400 hover:text-gray-700 px-2" @click="closePatient">
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
          </div>
          <div class="p-4">
            <Patient_Drawer
              :key="historyPatient.mbi"
              :patient-identifier="historyPatient.mbi"
              :entity-id="historyPatient.id ? Number(historyPatient.id) : null"
              :patient-name="historyPatient.name"
              :enabled-tabs="drawerEnabledTabs"
              default-tab="programs"
            />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { vMaska } from 'maska/vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import TaskCalendarPicker from '@/components/shared/TaskCalendarPicker.vue';
import { searchPatients } from '@/services/api/patientSearch';
import { enrollPatient } from '@/services/api/opportunities';

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function toIsoDob(dob) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dob);
  if (!match) return null;
  const [, month, day, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() !== Number(month) - 1
    || date.getUTCDate() !== Number(day)
  ) return null;
  return `${year}-${month}-${day}`;
}

export default {
  name: 'CareManagement_SearchEnroll',
  directives: { maska: vMaska },
  components: { FontAwesomeIcon, Patient_Drawer, TaskCalendarPicker },
  data() {
    return {
      historyPatient: null,
      searchText: '',
      searchDob: '',
      lastQuery: '',
      searching: false,
      hasSearched: false,
      results: [],
      enrollMessage: '',
      enrollSuccess: false,
      // Manual enrollment metadata modal
      showEnrollModal: false,
      enrolling: false,
      enrollPatientRef: null,
      enrollSlug: '',
      enrollForm: {},
      enrollDueDate: '',
      showEnrollDueCalendar: false,
      enrollDueCalendarPosition: {},
      // Contract-driven field list (from the metric's opportunity primitive DA)
      contractLoading: false,
      contractFields: [],
      // Test patient builder
      showTestModal: false,
      creatingTest: false,
      testPractices: [],
      selectedPracticeId: '',
      testPatients: [],
      deletingId: null,
      testForm: this.blankTestForm(),
      // Loaded per-tenant from the backend (tenant's CM metrics) — never hardcoded.
      availablePrograms: [],
      // Manual patient (not in attribution/claims)
      showManualModal: false,
      manualForm: this.blankManualForm(),
      showManualDueCalendar: false,
      manualDueCalendarPosition: {},
      manualError: '',
      manualSaving: false,
      parentQuery: '',
      parentResults: [],
      parentSearching: false,
      selectedParentName: '',
      parentDebounce: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['isTenantAdmin', 'current_tenant_slug']),
    drawerEnabledTabs() {
      const base = ['details', 'programs', 'engagement', 'care_plan', 'care_graph'];
      return this.current_tenant_slug === 'ihci' ? base.filter(t => t !== 'care_plan') : base;
    },
    manualValid() {
      const f = this.manualForm;
      return !!(f.last_name && f.patient_identifier && f.parent_entity_id && f.metric_id && f.first_task_due_date);
    },
    isAdmin() {
      return this.isTenantAdmin || !!this.current_user?.is_superuser;
    },
    canSubmitTest() {
      const f = this.testForm;
      return f.first_name.trim() && f.last_name.trim() && f.dob && f.program_slug && f.npi && f.tin;
    },
    selectedPractice() {
      return this.testPractices.find(p => p.practice_id === this.selectedPracticeId) || null;
    },
    enrollProgramLabel() {
      return this.availablePrograms.find(p => p.slug === this.enrollSlug)?.label || this.enrollSlug;
    },
  },
  methods: {
    positionDueCalendar(triggerRef, positionKey) {
      const trigger = this.$refs[triggerRef];
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(320, window.innerWidth - margin * 2);
      const height = 360;
      const left = Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin);
      const below = rect.bottom + margin + height <= window.innerHeight;
      const top = below ? rect.bottom + margin : Math.max(margin, rect.top - height - margin);
      this[positionKey] = { left: `${left}px`, top: `${top}px`, width: `${width}px` };
    },
    toggleManualDueCalendar() {
      if (this.showManualDueCalendar) {
        this.showManualDueCalendar = false;
        return;
      }
      this.positionDueCalendar('manualDueDateTrigger', 'manualDueCalendarPosition');
      this.showManualDueCalendar = true;
    },
    toggleEnrollDueCalendar() {
      if (this.showEnrollDueCalendar) {
        this.showEnrollDueCalendar = false;
        return;
      }
      this.positionDueCalendar('enrollDueDateTrigger', 'enrollDueCalendarPosition');
      this.showEnrollDueCalendar = true;
    },
    openPatient(patient) {
      // Open the shared drawer to the patient's history. Only meaningful when
      // the patient already exists as an entity (patient.id); enroll first
      // otherwise (no history to show yet).
      if (!patient.id) return;
      this.historyPatient = patient;
    },
    closePatient() {
      this.historyPatient = null;
    },
    blankManualForm() {
      return {
        first_name: '',
        last_name: '',
        dob: '',
        patient_identifier: '',
        parent_entity_id: null,
        metric_id: null,
        first_task_due_date: '',
      };
    },
    openManualModal() {
      this.manualForm = this.blankManualForm();
      // Carry over whatever they searched for — they typed it once already.
      const q = (this.searchText || '').trim();
      if (q && !/\d{3}/.test(q)) {
        const parts = q.replace(',', ' ').split(/\s+/).filter(Boolean);
        this.manualForm.last_name = parts[0] || '';
        this.manualForm.first_name = parts.slice(1).join(' ');
      }
      if (this.searchDob && /^\d{2}\/\d{2}\/\d{4}$/.test(this.searchDob)) {
        const [m, d, y] = this.searchDob.split('/');
        this.manualForm.dob = `${y}-${m}-${d}`;
      }
      this.parentQuery = '';
      this.parentResults = [];
      this.selectedParentName = '';
      this.manualError = '';
      this.showManualDueCalendar = false;
      this.showManualModal = true;
    },
    onParentQuery() {
      // Typing again after picking means they want a different parent.
      this.manualForm.parent_entity_id = null;
      this.selectedParentName = '';
      clearTimeout(this.parentDebounce);
      const q = this.parentQuery.trim();
      if (q.length < 2) {
        this.parentResults = [];
        return;
      }
      this.parentDebounce = setTimeout(() => this.searchParents(q), 250);
    },
    async searchParents(q) {
      this.parentSearching = true;
      try {
        // The dedicated search action, not a ?search= on list: it matches name,
        // identifiers AND parent names, returns breadcrumbs, and honours
        // entity-restricted users' allowed_entity_ids.
        const { data } = await axios.get('/api/entities/search/', {
          params: { search: q, limit: 20 },
        });
        const rows = data.results || [];
        // A patient cannot parent a patient.
        this.parentResults = rows.filter(
          (e) => (e.entity_type_name || e.entity_type?.type_name) !== 'Patient',
        );
      } catch {
        this.parentResults = [];
      } finally {
        this.parentSearching = false;
      }
    },
    pickParent(ent) {
      this.manualForm.parent_entity_id = ent.id;
      this.selectedParentName = ent.name;
      this.parentResults = [];
      this.parentQuery = ent.name;
    },
    async submitManual() {
      if (!this.manualValid || this.manualSaving) return;
      this.manualSaving = true;
      this.manualError = '';
      const f = this.manualForm;
      try {
        await enrollPatient({
          patient_identifier: f.patient_identifier,
          metric_id: f.metric_id,
          parent_entity_id: f.parent_entity_id,
          bene_last_name: f.last_name,
          bene_first_name: f.first_name,
          bene_dob: f.dob || null,
          first_task_due_date: f.first_task_due_date,
          metadata: {
            bene_last_name: f.last_name,
            bene_first_name: f.first_name,
            bene_dob: f.dob || null,
            manually_added: true,
          },
        });
        this.showManualModal = false;
        this.enrollSuccess = true;
        this.enrollMessage = `${f.last_name} added and enrolled.`;
        setTimeout(() => { this.enrollMessage = ''; }, 4000);
        // Re-run the search so the new patient is visible where they looked.
        this.searchText = f.patient_identifier;
        this.searchDob = '';
        await this.doSearch();
      } catch (err) {
        this.manualError = err?.response?.data?.message
          || err?.response?.data?.detail
          || 'Could not create the patient.';
      } finally {
        this.manualSaving = false;
      }
    },
    blankTestForm() {
      return {
        first_name: '',
        last_name: '',
        dob: '',
        program_slug: '',
        npi: '',
        tin: '',
        episode_type: 'INPATIENT',
        discharge_time: nowLocalISO(),
        risk_score: null,
        facility: 'Test Hospital',
      };
    },
    openTestModal() {
      this.testForm = this.blankTestForm();
      this.selectedPracticeId = '';
      // Default to the first program if loaded
      if (this.availablePrograms.length) {
        this.testForm.program_slug = this.availablePrograms[0].slug;
      }
      this.showTestModal = true;
      if (!this.testPractices.length) this.loadTestPractices();
      if (!this.availablePrograms.length) this.loadPrograms();
    },
    onPracticeChange() {
      const pr = this.selectedPractice;
      this.testForm.npi = pr ? pr.npi : '';
      this.testForm.tin = pr ? pr.tin : '';
    },
    async loadTestPractices() {
      try {
        const resp = await axios.get('/api/care-management/test-patient/practices/');
        this.testPractices = resp.data || [];
      } catch {
        this.testPractices = [];
      }
    },
    async loadPrograms() {
      try {
        const resp = await axios.get('/api/metrics/', { params: { metric_category: 'Care Management' } });
        this.availablePrograms = (resp.data?.results || resp.data || []).map(m => ({
          slug: m.metric_name_slug,
          label: m.name,
          id: m.id,
        }));
      } catch {
        this.availablePrograms = [];
      }
    },
    async createTestPatient() {
      if (!this.canSubmitTest) return;
      this.creatingTest = true;
      try {
        await axios.post('/api/care-management/test-patient/', { ...this.testForm });
        this.showToast(`Created test patient ${this.testForm.last_name}, ${this.testForm.first_name}`, true);
        this.showTestModal = false;
        await this.loadTestPatients();
      } catch (err) {
        this.showToast(err.response?.data?.message || 'Creation failed', false);
      } finally {
        this.creatingTest = false;
      }
    },
    async loadTestPatients() {
      if (!this.isAdmin) return;
      try {
        const resp = await axios.get('/api/care-management/test-patient/');
        this.testPatients = resp.data || [];
      } catch {
        this.testPatients = [];
      }
    },
    async deleteTestPatient(tp) {
      if (!confirm(`Delete test patient ${tp.name}? This removes the opportunity, tasks, work, actions, and entity.`)) return;
      this.deletingId = tp.opportunity_id;
      try {
        await axios.delete(`/api/care-management/test-patient/${tp.opportunity_id}/`);
        this.showToast(`Deleted ${tp.name}`, true);
        await this.loadTestPatients();
      } catch (err) {
        this.showToast(err.response?.data?.message || 'Delete failed', false);
      } finally {
        this.deletingId = null;
      }
    },
    formatDate(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    },

    async doSearch() {
      const q = this.searchText.trim();
      const dob = toIsoDob(this.searchDob) || undefined;
      if (!q && !dob) return;

      this.searching = true;
      this.hasSearched = true;
      this.lastQuery = [q, dob && `DOB: ${this.searchDob}`].filter(Boolean).join(' · ');

      try {
        const patients = await searchPatients({
          ...(q ? { q } : {}),
          ...(dob ? { dob } : {}),
        });
        this.results = (patients || []).map(p => ({
          id: p.entity_id,
          name: [p.last_name, p.first_name].filter(Boolean).join(', ') || p.mbi,
          mbi: p.mbi,
          // Snowflake may return a midnight timestamp for a date-only DOB.
          // Keep only the ISO date so the UI never displays a misleading time.
          dob: p.dob ? p.dob.slice(0, 10) : null,
          phone: p.phone || '',
          npi: p.npi,
          tin: p.tin || null,
          practice: p.practice_name || '',
          practice_name: p.practice_name || '',
          attributed: !!p.attributed,
          attributed_contract_id: p.attributed_contract_id || null,
          active_programs: [],
          previous_programs: [],
          selectedProgram: '',
        }));

        if (this.results.length > 0) {
          await this.loadActivePrograms();
        }
      } catch (err) {
        console.error('Search error:', err);
        this.results = [];
      } finally {
        this.searching = false;
      }
    },

    async loadActivePrograms() {
      const mbis = this.results.map(r => r.mbi).filter(Boolean);
      if (!mbis.length) return;

      try {
        const resp = await axios.post('/api/opportunities/batch-check-enrollment/', {
          patient_identifiers: mbis,
        });
        const enrollment = resp.data || {};
        for (const patient of this.results) {
          const e = enrollment[patient.mbi] || {};
          patient.active_programs = e.active || [];
          patient.previous_programs = e.previous || [];
        }
      } catch {
        // Non-critical
      }
    },

    // Default value per field type for a fresh form.
    defaultForField(field) {
      if (field.type === 'boolean') return false;
      if (field.type === 'datetime') {
        // Pre-fill the primary discharge/event time so the common case is one step.
        return field.key === 'discharge_time' ? nowLocalISO() : '';
      }
      if (field.type === 'number') return null;
      return '';
    },
    async openEnrollModal(patient) {
      if (!patient.selectedProgram) return;
      this.enrollPatientRef = patient;
      this.enrollSlug = patient.selectedProgram;
      this.enrollForm = {};
      this.enrollDueDate = '';
      this.showEnrollDueCalendar = false;
      this.contractFields = [];
      this.contractLoading = true;
      this.showEnrollModal = true;
      // Reset the row's dropdown; the modal now owns the selection.
      patient.selectedProgram = '';

      // Fetch the field contract for this metric's opportunity primitive.
      try {
        const { data } = await axios.get('/api/opportunities/enroll-contract/', {
          params: { metric_name_slug: this.enrollSlug },
        });
        this.contractFields = data.fields || [];
        const form = {};
        for (const f of this.contractFields) form[f.key] = this.defaultForField(f);
        this.enrollForm = form;
      } catch {
        this.showToast('Could not load enrollment fields', false);
        this.contractFields = [];
        this.enrollForm = {};
      } finally {
        this.contractLoading = false;
      }
    },
    cancelEnroll() {
      this.showEnrollModal = false;
      this.enrollPatientRef = null;
      this.enrollSlug = '';
      this.enrollForm = {};
      this.enrollDueDate = '';
      this.showEnrollDueCalendar = false;
      this.contractFields = [];
    },
    async confirmEnroll() {
      const patient = this.enrollPatientRef;
      const slug = this.enrollSlug;
      if (!patient || !slug || !this.enrollDueDate) return;
      const metric = this.availablePrograms.find(p => p.slug === slug);
      if (!metric) return;

      const progLabel = this.enrollProgramLabel;
      this.enrolling = true;

      // Build the opportunity metadata generically from the contract fields.
      // Keys map 1:1 to the metric's opportunity primitive OBJECT_CONSTRUCT, so
      // the worklist renders a hand-enrolled opp exactly like a synced one.
      const metadata = {};
      for (const field of this.contractFields) {
        let v = this.enrollForm[field.key];
        if (v === null || v === undefined || v === '') continue;
        if (field.type === 'datetime' && typeof v === 'string') {
          v = v.replace('T', ' ');
        }
        if (typeof v === 'string') v = v.trim();
        metadata[field.key] = v;
      }

      try {
        const payload = {
          patient_identifier: patient.mbi,
          metric_id: metric.id,
          first_task_due_date: this.enrollDueDate,
          metadata,
        };
        if (patient.id) payload.entity_id = patient.id;
        // Search-resolved identity/provider fields — fixed, sent alongside metadata.
        if (patient.npi) payload.npi = patient.npi;
        if (patient.tin) payload.tin = patient.tin;
        if (patient.dob) payload.bene_dob = patient.dob;
        if (patient.name) {
          const parts = patient.name.split(', ');
          if (parts.length === 2) {
            payload.bene_last_name = parts[0];
            payload.bene_first_name = parts[1];
          }
        }
        await enrollPatient(payload);

        patient.active_programs.push(progLabel);
        this.showToast(`Enrolled ${patient.name} in ${progLabel}. Computing priority flags...`, true);
        this.cancelEnroll();
      } catch (err) {
        const msg = err.response?.data?.message || 'Enrollment failed';
        this.showToast(msg, false);
      } finally {
        this.enrolling = false;
      }
    },

    showToast(message, success) {
      this.enrollMessage = message;
      this.enrollSuccess = success;
      setTimeout(() => { this.enrollMessage = ''; }, 4000);
    },
  },

  mounted() {
    this.$refs.searchInput?.focus();
    this.loadPrograms();
    this.loadTestPatients();
  },
};
</script>
